import React, { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_BTN_PRIMARY,
  AE_CARD,
  AE_EDITABLE_INPUT,
  AE_ICON_SIZE,
  AE_LABEL,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_SELECT,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeTemplateDetail, AeTemplateLogicRule, AeTemplateSection } from '@/types/assessmentEngine';
import { BuilderIconButton } from './BuilderIconButton';

interface LogicRulesEditorProps {
  template: AeTemplateDetail;
  readOnly?: boolean;
  onReload: () => void;
}

interface FlatSection {
  id: string;
  label: string;
}

interface ChoiceQuestion {
  id: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

function walkSections(sections: AeTemplateSection[], depth = 0): FlatSection[] {
  const out: FlatSection[] = [];
  for (const sec of sections) {
    const prefix = depth > 0 ? '— '.repeat(depth) : '';
    out.push({ id: sec.id, label: `${prefix}${sec.name}` });
    out.push(...walkSections(sec.subsections, depth + 1));
  }
  return out;
}

function collectChoiceQuestions(sections: AeTemplateSection[]): ChoiceQuestion[] {
  const out: ChoiceQuestion[] = [];
  const walk = (list: AeTemplateSection[]) => {
    for (const sec of list) {
      for (const q of sec.questions) {
        if (q.question_type === 'single_choice' || q.question_type === 'radio') {
          out.push({
            id: q.id,
            label: q.question_text || q.question_key,
            options: q.options.map((o) => ({ value: o.option_value, label: o.option_label })),
          });
        }
      }
      walk(sec.subsections);
    }
  };
  walk(sections);
  return out;
}

function ruleSummaryText(
  rule: AeTemplateLogicRule,
  sections: FlatSection[],
  questions: ChoiceQuestion[],
  t: (key: string) => string
): string {
  const src = questions.find((q) => q.id === rule.source_question_id);
  const tgt = sections.find((s) => s.id === rule.target_section_id);
  const val = (rule.condition_values as unknown[])?.[0] ?? '';
  const actionLabel =
    rule.action === 'hide'
      ? t('assessmentEngine.builder.logicActionHide')
      : t('assessmentEngine.builder.logicActionSkip');
  const timing =
    rule.action === 'skip' && rule.trigger_scope && rule.trigger_scope !== 'question'
      ? ` · ${t('assessmentEngine.builder.logicJumpTiming')}: ${t(
          rule.trigger_scope === 'subsection'
            ? 'assessmentEngine.builder.logicJumpAfterSubsection'
            : 'assessmentEngine.builder.logicJumpAfterSection'
        )}`
      : rule.action === 'skip'
        ? ` · ${t('assessmentEngine.builder.logicJumpTiming')}: ${t('assessmentEngine.builder.logicJumpAfterQuestion')}`
        : '';
  return `${t('assessmentEngine.builder.logicSourceQuestion')} ${src?.label ?? '?'} ${t(
    'assessmentEngine.builder.logicWhenValue'
  ).toLowerCase()} ${val} → ${actionLabel} ${t('assessmentEngine.builder.logicTargetSection').toLowerCase()} ${tgt?.label ?? '?'}${timing}`;
}

/** If question equals value → action on section (jump timing when action is jump). */
export const LogicRulesEditor: React.FC<LogicRulesEditorProps> = ({ template, readOnly, onReload }) => {
  const { t } = useLanguage();
  const sections = useMemo(() => walkSections(template.sections), [template.sections]);
  const choiceQuestions = useMemo(() => collectChoiceQuestions(template.sections), [template.sections]);
  const rules = template.logic_rules ?? [];

  const [sourceId, setSourceId] = useState('');
  const [value, setValue] = useState('');
  const [targetSectionId, setTargetSectionId] = useState('');
  const [action, setAction] = useState<'skip' | 'hide'>('skip');
  const [triggerScope, setTriggerScope] = useState<'subsection' | 'section'>('section');
  const [busy, setBusy] = useState(false);

  const selectedQuestion = choiceQuestions.find((q) => q.id === sourceId);

  const addRule = async () => {
    if (!sourceId || !value.trim() || !targetSectionId) return;
    setBusy(true);
    try {
      await assessmentEngineService.createLogicRule(template.id, {
        source_question_id: sourceId,
        operator: 'equals',
        condition_values: [value.trim()],
        target_section_id: targetSectionId,
        action,
        priority: 0,
        trigger_scope: action === 'skip' ? triggerScope : undefined,
      });
      setSourceId('');
      setValue('');
      setTargetSectionId('');
      setAction('skip');
      setTriggerScope('section');
      onReload();
    } finally {
      setBusy(false);
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!window.confirm(t('assessmentEngine.builder.confirmDeleteRule'))) return;
    setBusy(true);
    try {
      await assessmentEngineService.deleteLogicRule(template.id, ruleId);
      onReload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${AE_CARD} space-y-4`}>
      <h2 className={AE_SECTION_TITLE}>{t('assessmentEngine.builder.conditionalLogic')}</h2>
      <p className={AE_MUTED}>{t('assessmentEngine.builder.conditionalLogicHint')}</p>

      {rules.length > 0 && (
        <ul className="space-y-2">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="flex items-center justify-between gap-2 border border-[var(--color-border)] rounded-md px-3 py-2 text-sm text-[var(--color-surface-text)]"
            >
              <span>{ruleSummaryText(rule, sections, choiceQuestions, t)}</span>
              {!readOnly && (
                <BuilderIconButton
                  label={t('assessmentEngine.builder.remove')}
                  variant="danger"
                  disabled={busy}
                  onClick={() => void deleteRule(rule.id)}
                >
                  <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                </BuilderIconButton>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="space-y-3 border-t border-[var(--color-border)] pt-4">
          <p className="text-sm font-medium text-[var(--color-surface-text)]">
            {t('assessmentEngine.builder.conditionalLogicPattern')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.builder.logicSourceQuestion')}</label>
              <select className={AE_SELECT} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                <option value="">{t('assessmentEngine.builder.selectQuestion')}</option>
                {choiceQuestions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.builder.logicWhenValue')}</label>
              {selectedQuestion && selectedQuestion.options.length > 0 ? (
                <select className={AE_SELECT} value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="">{t('assessmentEngine.builder.selectValue')}</option>
                  {selectedQuestion.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={AE_EDITABLE_INPUT}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t('assessmentEngine.builder.logicValuePlaceholder')}
                />
              )}
            </div>
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.builder.logicAction')}</label>
              <select
                className={AE_SELECT}
                value={action}
                onChange={(e) => setAction(e.target.value as 'skip' | 'hide')}
              >
                <option value="skip">{t('assessmentEngine.builder.logicActionSkip')}</option>
                <option value="hide">{t('assessmentEngine.builder.logicActionHide')}</option>
              </select>
            </div>
            <div>
              <label className={AE_LABEL}>{t('assessmentEngine.builder.logicTargetSection')}</label>
              <select
                className={AE_SELECT}
                value={targetSectionId}
                onChange={(e) => setTargetSectionId(e.target.value)}
              >
                <option value="">{t('assessmentEngine.builder.selectSection')}</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            {action === 'skip' && (
              <div>
                <label className={AE_LABEL}>{t('assessmentEngine.builder.logicJumpTiming')}</label>
                <select
                  className={AE_SELECT}
                  value={triggerScope}
                  onChange={(e) => setTriggerScope(e.target.value as 'subsection' | 'section')}
                >
                  <option value="subsection">{t('assessmentEngine.builder.logicJumpAfterSubsection')}</option>
                  <option value="section">{t('assessmentEngine.builder.logicJumpAfterSection')}</option>
                </select>
              </div>
            )}
            <div className="flex items-end md:col-span-2">
              <button
                type="button"
                className={AE_BTN_PRIMARY}
                disabled={busy || !sourceId || !value || !targetSectionId}
                onClick={() => void addRule()}
              >
                <PlusIcon className={`${AE_ICON_SIZE} inline mr-1`} aria-hidden />
                {t('assessmentEngine.builder.addLogicRule')}
              </button>
            </div>
          </div>
        </div>
      )}

      {choiceQuestions.length === 0 && (
        <p className={AE_MUTED}>{t('assessmentEngine.builder.logicNeedsChoiceQuestion')}</p>
      )}
    </div>
  );
};
